// /top-movies/:slug — someone's shared all-time Top 5 / Top 10. Public, no
// account needed. The point of the page is the next step: "Create yours",
// which carries the slug along as the referral for sign-up attribution.
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { EmptyShareState, PublicShareLayout } from '../../components/PublicShare/PublicShareLayout';
import TopMoviesGrid from '../../components/TopMovies/TopMoviesGrid';
import TopMoviesSharePanel from '../../components/TopMovies/TopMoviesSharePanel';
import { TopMoviesService } from '../../services/topMoviesService';
import { useAuth } from '../../context/AuthContext';
import { useShareMetadata } from '../../utils/shareMetadata';
import { setReferral } from '../../attribution/referral';
import { ANALYTICS_EVENTS } from '../../analytics';
import { trackTopMovies } from '../../analytics/topMoviesAnalytics';
import styles from './PublicTopMovies.module.css';

export function SharedTopMoviesView({ payload, viewerId, openShare = false }) {
    const { list, owner, movies, slug } = payload;
    const ownerName = owner.displayName || `@${owner.username}`;
    const isOwner = Boolean(viewerId) && viewerId === owner.id;
    const [showShare, setShowShare] = useState(openShare);

    return (
        <PublicShareLayout>
            <header className={styles.hero}>
                <span className={styles.eyebrow}>
                    <Link to={`/u/${owner.username}`}>{ownerName}</Link>&apos;s
                </span>
                <h1>All-Time Top {list.size}</h1>
                <p className={styles.sub}>{list.ranked ? 'Ranked, #1 first.' : 'In no particular order.'}</p>
            </header>

            <TopMoviesGrid movies={movies} ranked={list.ranked} size={list.size} />

            <div className={styles.actions}>
                {isOwner ? (
                    <>
                        <Link to="/top-movies" className={styles.primary}>Edit my Top Movies</Link>
                        <button type="button" className={styles.secondary} onClick={() => setShowShare((v) => !v)}>{showShare ? 'Hide sharing' : 'Share'}</button>
                    </>
                ) : (
                    <button type="button" className={styles.secondary} onClick={() => setShowShare((v) => !v)}>{showShare ? 'Hide sharing' : 'Share this list'}</button>
                )}
            </div>

            {showShare && (
                <section className={styles.sharePanel} aria-label="Share this list">
                    <TopMoviesSharePanel
                        movies={movies}
                        size={list.size}
                        ranked={list.ranked}
                        shared={{ slug, shareUrl: payload.shareUrl }}
                        loggedIn={Boolean(viewerId)}
                        sourceScreen={isOwner ? 'public_page_owner' : 'public_page'}
                    />
                </section>
            )}

            {!isOwner && (
                <section className={styles.cta}>
                    <div>
                        <p className={styles.ctaKicker}>Your turn</p>
                        <h2>Think your Top {list.size} is better? Create yours.</h2>
                        <p>Pick your all-time favorites, rank them, and get a poster to share. Free — no account needed to start.</p>
                    </div>
                    <Link
                        to={`/top-movies?ref=${encodeURIComponent(slug)}`}
                        className={styles.primary}
                        onClick={() => trackTopMovies(ANALYTICS_EVENTS.TOP_MOVIES_CREATE_YOURS_CLICKED, { size: list.size, ref: slug, logged_in: Boolean(viewerId) }, { listSlug: slug })}
                    >
                        Create your Top {list.size}
                    </Link>
                </section>
            )}
            <p className={styles.attribution}>Movie data and posters from TMDB. This product uses the TMDB API but is not endorsed or certified by TMDB.</p>
        </PublicShareLayout>
    );
}

export default function PublicTopMovies() {
    const { slug } = useParams();
    const { user } = useAuth();
    const location = useLocation();
    const [payload, setPayload] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        TopMoviesService.getShared(slug)
            .then((data) => {
                if (!mounted) return;
                setPayload(data);
                if (!data.isPrivate) {
                    const isOwner = user?.id && user.id === data.owner?.id;
                    // A visitor who later signs up is attributed to this list.
                    if (!isOwner) setReferral('top_movies', data.slug);
                    trackTopMovies(
                        ANALYTICS_EVENTS.TOP_MOVIES_PUBLIC_PAGE_VIEWED,
                        { size: data.list.size, ranked: data.list.ranked, logged_in: Boolean(user), is_owner: Boolean(isOwner), ref: data.slug },
                        { listSlug: data.slug, onceKey: data.slug },
                    );
                }
            })
            .catch((err) => mounted && setError(err.message || 'List not found'));
        return () => { mounted = false; };
    }, [slug, user]);

    const metadata = useMemo(() => {
        if (!payload || payload.isPrivate) return { title: 'Top Movies | Aluvo', description: 'An all-time Top Movies list on Aluvo.', url: `${window.location.origin}/top-movies/${slug}` };
        const ownerName = payload.owner.displayName || `@${payload.owner.username}`;
        return {
            title: `${ownerName}'s All-Time Top ${payload.list.size} | Aluvo`,
            description: `${ownerName}'s all-time Top ${payload.list.size}: ${payload.movies.slice(0, 3).map((m) => m.title).join(', ')}… What's yours?`,
            url: payload.shareUrl,
            image: payload.cardUrl,
            type: 'article',
        };
    }, [payload, slug]);
    useShareMetadata(metadata);

    if (error) return <EmptyShareState icon="🎬" title="List not found" message="This Top Movies list does not exist or is no longer shared." />;
    if (!payload) return <EmptyShareState icon="◎" title="Loading list" message="Rolling the opening credits…" />;
    if (payload.isPrivate) return <EmptyShareState icon="🔒" title="This list is private." message="The owner has turned off sharing for their Top Movies." />;
    return <SharedTopMoviesView payload={payload} viewerId={user?.id} openShare={Boolean(location.state?.share)} />;
}
